/**
 * File Geodatabase import, over the API.
 *
 * WHY THIS ONE FORMAT LEAVES THE BROWSER
 * Everything else `localFile.ts` reads is parsed here with platform built-ins
 * and no dependency. A .gdb cannot be: it is Esri's proprietary binary format,
 * and the only reader for it is GDAL's OpenFileGDB driver. Running that in a
 * tab means shipping a GDAL WebAssembly build — ten to twenty megabytes, to
 * every visitor, most of whom will never open a geodatabase. So the archive
 * goes to the API, real GDAL reads it, and what comes back is the same plain
 * GeoJSON every other importer here produces.
 *
 * WHY THE UPLOAD AND THE LAYERS ARE SEPARATE CALLS
 * A geodatabase holds several feature classes and the user usually wants some
 * of them. Uploading once and then fetching only what was ticked means a 200 MB
 * file is transferred once, the picker appears in a second, and the feature
 * classes nobody selected are never converted at all.
 *
 * THE PRIVACY POINT, STATED PLAINLY
 * This is the only local format whose contents go to the server. It is the
 * user's own BlueView API, behind their own session, and the extracted copy is
 * deleted on a short TTL — but it does leave the machine, which is why the
 * dialog says so before the upload starts rather than after.
 */

import { api, ApiError } from '../services/apiClient';

/** One feature class, as described before any geometry is converted. */
export interface GdbLayerSummary {
  index: number;
  name: string;
  geometryType: 'point' | 'line' | 'polygon' | 'unknown';
  /**
   * Whether the layer has a geometry column at all — a different question from
   * whether `geometryType` was recognised. Geodatabases carry plain attribute
   * tables alongside their feature classes, and those can never be mapped
   * however many rows they hold.
   */
  hasGeometry: boolean;
  featureCount: number;
  fields: string[];
  /** How the source coordinate system will be described in a notice. */
  crsName: string;
  bounds: [number, number, number, number] | null;
  notices: string[];
}

export interface GdbUpload {
  token: string;
  source: string;
  /** The .gdb (or .gpkg) the server actually found inside the archive. */
  dataset: string;
  expiresInSeconds: number;
  maxFeaturesPerLayer: number;
  layers: GdbLayerSummary[];
}

export interface GdbLayerData {
  name: string;
  geometryType: 'point' | 'line' | 'polygon' | 'unknown';
  featureCount: number;
  fields: string[];
  notices: string[];
  data: GeoJSON.FeatureCollection;
}

/**
 * True when this ZIP holds a geodatabase rather than a shapefile.
 *
 * Read off the entry names, so a `.zip` someone renamed still lands on the
 * right path. A `.gdb` is a directory, so its files always appear under a
 * `something.gdb/` prefix; `.gdbtable` covers the case where the folder's
 * CONTENTS were zipped instead of the folder.
 */
export function looksLikeGeodatabase(entryNames: string[]): boolean {
  return entryNames.some(
    (name) =>
      /(^|\/)[^/]+\.gdb\//i.test(name) || /\.gdbtable$/i.test(name) || /\.gpkg$/i.test(name),
  );
}

/** Turns an ApiError into something worth showing above a file picker. */
function describe(error: unknown, fallback: string): Error {
  if (error instanceof ApiError) {
    if (error.unauthenticated) {
      return new Error('Your session has expired. Sign in again to import a geodatabase.');
    }
    if (error.status === 413) return new Error(error.message);
    if (error.status === 501) {
      return new Error(
        `${error.message} Convert the geodatabase to GeoJSON, or publish it through ` +
          'GeoServer or ArcGIS.',
      );
    }
    if (error.status === 0) {
      return new Error(
        'The geodatabase could not be uploaded — the server was unreachable. ' +
          'Check your connection and try again.',
      );
    }
    return new Error(error.message);
  }
  return new Error(fallback);
}

/** Uploads the zipped geodatabase and returns what is inside it. */
export async function uploadGeodatabase(file: File): Promise<GdbUpload> {
  const form = new FormData();
  form.append('file', file, file.name);

  try {
    return await api.postForm<GdbUpload>('/gdb/uploads', form);
  } catch (error) {
    throw describe(error, 'This geodatabase could not be read.');
  }
}

/** Converts one feature class to WGS84 GeoJSON. */
export async function fetchGeodatabaseLayer(
  token: string,
  index: number,
): Promise<GdbLayerData> {
  try {
    return await api.get<GdbLayerData>(`/gdb/uploads/${token}/layers/${index}`);
  } catch (error) {
    throw describe(error, 'This layer could not be converted.');
  }
}

/**
 * Tells the server the extracted copy is no longer needed.
 *
 * Deliberately swallows its errors. This is housekeeping the server would do
 * on its own TTL anyway, and it runs while the dialog is closing — surfacing a
 * failure here would replace a completed import with an alarming message about
 * something the user did not ask for and cannot act on.
 */
export async function releaseGeodatabase(token: string): Promise<void> {
  try {
    await api.delete<void>(`/gdb/uploads/${token}`);
  } catch {
    /* The TTL sweep will collect it. */
  }
}
